import React, { useEffect, useState } from 'react'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import { AnchorLink } from 'gatsby-plugin-anchor-links'

import SEO from '../components/seo'
import BaseDescriptionSection from '../components/BaseDescriptionSection'
import AboutSection from '../components/AboutSection'
import BenefitsSection from '../components/BenefitsSection'
import HowItWorksSection from '../components/HowItWorksSection'
import ClientsSection from '../components/ClientsSection'
import ModulesSection from '../components/ModulesSection'
import UpdatesSection from '../components/UpdatesSection'
import BannerSection from '../components/BannerSection'
import { Button } from '../components/Common/Button'
import Feedback from '../components/Feedback'
import PartnersBlock from '../components/PartnersBlock'
import SwitchfromAutodesktoSignalDocsBlock from '../components/SwitchfromAutodesktoSignalDocsBlock'

import { GlobalContext } from '../context/GlobalContext'
import { AppRegionEnum } from '../types/app-region.enum'
import { useTranslations } from '../hooks/use-translations'

import * as s from './pages.module.scss'
import BannerDynamicSection from '../components/BannerDynamicSection'
import RegistrySection from '../components/RegisterSection'
import ProductsSection from '../components/ProductsSection'
import { isBrowser } from '../utils'
import ContactForm, { IContactModalState } from '../components/contact-form'

const IndexPage: React.FC = () => {
  const { region, location } = React.useContext(GlobalContext)
  const images = useStaticQuery(imagesQuery)
  const { t, getPath } = useTranslations()
  const locationState = location?.state
  const [modal, setModal] = useState<IContactModalState>()
  const carouselBannerData = [
    {
      imageDesktop: images[`banner3_ru`].childImageSharp.gatsbyImageData,
      imageMobile: images[`banner3_mobile_ru`].childImageSharp.gatsbyImageData,
      title: t('Home title 3'),
      subTitle: t('Home subtitle 3'),
      children: (
        <Button
          className={s.bannerButton}
          onClick={() => navigate(getPath('/news/signal-on-premise'))}
        >
          {t('More')}
        </Button>
      ),
    },
    {
      imageDesktop: images[`banner2_ru`].childImageSharp.gatsbyImageData,
      imageMobile: images[`banner2_mobile_ru`].childImageSharp.gatsbyImageData,
      title: t('Home title 2'),
      subTitle: t('Home subtitle 2'),
      highlightSubTitleWordsByIdx: [2, 3, 13, 14],
      children: (
        <AnchorLink
          to={getPath('/#how-it-works-section')}
          stripHash
          className={s.anchor_link}
        >
          <Button size="medium">{t('How our technology works')}</Button>
        </AnchorLink>
      ),
    },
    {
      imageDesktop: images[`banner_ru`].childImageSharp.gatsbyImageData,
      imageMobile: images[`banner_mobile_ru`].childImageSharp.gatsbyImageData,
      title: t('Home title'),
      children: (
        <AnchorLink
          to={getPath('/#how-it-works-section')}
          stripHash
          className={s.anchor_link}
        >
          <Button size="medium">{t('How our technology works')}</Button>
        </AnchorLink>
      ),
    },
  ]

  useEffect(() => {
    if (isBrowser()) {
      if (locationState?.tryModalOpened) {
        setModal({
          title: t('Request a demo of Signal'),
          requestType: 'Demo',
        })
        navigate('/', { state: {} })
      }
      if (locationState?.testingModalOpened) {
        setModal({
          title: t('Request testing'),
          requestType: 'Trial',
        })
        navigate('/', { state: {} })
      }
    }
  }, [locationState, t])

  return (
    <>
      {modal && (
        <ContactForm
          onClose={() => setModal(undefined)}
          title={modal.title}
          requestType={modal.requestType}
        />
      )}
      <SEO
        title={t('_title')}
        description={t('seo_description')}
        keywords={region === AppRegionEnum.Us ? t('keywords_main') : undefined}
      />

      {region === AppRegionEnum.Us ? (
        <BannerSection
          imageDesktop={
            images[`banner_${region}`].childImageSharp.gatsbyImageData
          }
          imageMobile={
            images[`banner_mobile_${region}`].childImageSharp.gatsbyImageData
          }
          title={t('Home title')}
          colorShadow={'light'}
          titleSize={40}
          isUppercase={false}
        >
          <AnchorLink
            to={getPath('/#how-it-works-section')}
            stripHash
            className={s.anchor_link}
          >
            <Button size="medium">{t('How our technology works')}</Button>
          </AnchorLink>
        </BannerSection>
      ) : (
        <BannerDynamicSection
          carouselData={carouselBannerData}
          colorShadow={'light'}
          titleSize={40}
          isUppercase={false}
          classNameTitle={s.bannerTitle}
          classNameSubTitle={s.bannerSubtitle}
          classNameHighlightSubTitle={s.bannerDigits}
        />
      )}
      {region === AppRegionEnum.Ru && <RegistrySection />}
      {region === AppRegionEnum.Ru && <BaseDescriptionSection />}
      {region === AppRegionEnum.Us && <ProductsSection />}
      <BenefitsSection />
      {region === AppRegionEnum.Ru && <ModulesSection />}
      {region === AppRegionEnum.Ru && <SwitchfromAutodesktoSignalDocsBlock />}
      <HowItWorksSection />
      {region === AppRegionEnum.Ru ? <Feedback /> : <ClientsSection />}
      <UpdatesSection />
      {region === AppRegionEnum.Ru && <PartnersBlock />}
      <AboutSection />
    </>
  )
}

export default IndexPage

const imagesQuery = graphql`
  query {
    banner_ru: file(relativePath: { eq: "home/signal-banner.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    banner_mobile_ru: file(
      relativePath: { eq: "home/signal-banner-mobile.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    banner2_ru: file(relativePath: { eq: "home/signal-banner2.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    banner2_mobile_ru: file(
      relativePath: { eq: "home/signal-banner-mobile2.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    banner3_ru: file(relativePath: { eq: "home/signal-banner3.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    banner3_mobile_ru: file(
      relativePath: { eq: "home/signal-banner-mobile3.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    banner_us: file(relativePath: { eq: "home/signax-banner.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    banner_mobile_us: file(
      relativePath: { eq: "home/signax-banner-mobile.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
  }
`
